import type { AgentChatAttachment } from "@src/components/agent-chat/types";
import {
	type Dispatch,
	type ReactNode,
	type SetStateAction,
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

type AgentChatComposerDraftContextValue = {
	draft: string;
	setDraft: Dispatch<SetStateAction<string>>;
	attachments: AgentChatAttachment[];
	setAttachments: Dispatch<SetStateAction<AgentChatAttachment[]>>;
	attachmentError: string | null;
	setAttachmentError: Dispatch<SetStateAction<string | null>>;
	clearComposerDraft: () => void;
};

const AgentChatComposerDraftContext =
	createContext<AgentChatComposerDraftContextValue | null>(null);

export function AgentChatComposerDraftProvider({
	children,
}: {
	children: ReactNode;
}) {
	const [draft, setDraft] = useState("");
	const [attachments, setAttachments] = useState<AgentChatAttachment[]>([]);
	const [attachmentError, setAttachmentError] = useState<string | null>(null);

	const clearComposerDraft = useCallback(() => {
		setDraft("");
		setAttachments([]);
		setAttachmentError(null);
	}, []);

	const value = useMemo(
		() => ({
			draft,
			setDraft,
			attachments,
			setAttachments,
			attachmentError,
			setAttachmentError,
			clearComposerDraft,
		}),
		[attachmentError, attachments, clearComposerDraft, draft],
	);

	return (
		<AgentChatComposerDraftContext.Provider value={value}>
			{children}
		</AgentChatComposerDraftContext.Provider>
	);
}

export function useAgentChatComposerDraft(): AgentChatComposerDraftContextValue {
	const context = useContext(AgentChatComposerDraftContext);
	if (!context) {
		throw new Error(
			"useAgentChatComposerDraft must be used within AgentChatComposerDraftProvider",
		);
	}
	return context;
}
